/**
 * Lista de plantillas (`/activa/oficial/plantillas`).
 *
 * Los filtros son en el cliente: el backend filtra por UN estado y «En revisión» son dos
 * (`received` + `pending`), y con un tope de 250 plantillas por marca no hace falta paginar. Sólo
 * «Mostrar archivadas» vuelve a pedir, porque las archivadas no vienen por defecto.
 *
 * Con alguna plantilla en revisión, la lista se refresca cada 30 s: el cambio a «Aprobada» tiene
 * que llegar a la pantalla sin que el operador recargue (CA b).
 *
 * «Aprobadas» son las que se pueden usar: una aprobada en simulado con el servidor en twilio, o una
 * de otra cuenta de Twilio (`motivoPlantillaInutilizable`), queda en «Todas» con su chip y un aviso
 * dice cuántas no aparecen.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, CheckCircle2, ExternalLink, FileText, Image as ImageIcon, LayoutPanelTop, MessageSquare, Plus, RefreshCw,
  Reply, Search, Sparkles, X,
} from "lucide-react";
import type { EstadoPlantilla, ListaPlantillas, PlantillaResumen, TipoPlantilla } from "../types";
import { oficialApi } from "../api";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { intervaloPlantillas } from "../logic/polling";
import { CATEGORIA, CHIP_PLANTILLA, motivoPlantillaInutilizable, motivoPlantillaNoUsable, TIPO_PLANTILLA } from "../logic/textos";
import { horasTexto, usdPorMensaje } from "../logic/formato";
import { enRevisionDeMeta, primeroEnRevision } from "../logic/plantilla";
import { usePolling } from "../hooks/usePolling";
import { useCuentaOficial } from "../contexto";
import { ChipEstadoPlantilla } from "../components/ChipEstadoPlantilla";
import { Tarjeta } from "../components/Tarjeta";
import { num, plural, pct } from "../../campana-nueva/utils/format";
import { SoloEquipo } from "../../campana-nueva/components/SoloEquipo";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";

const R = "/activa/oficial";

/** Filtros de estado de la pantalla → estados del backend. */
export const FILTROS_ESTADO: { clave: string; label: string; estados: EstadoPlantilla[] | null }[] = [
  { clave: "todas", label: "Todas", estados: null },
  { clave: "aprobadas", label: "Aprobadas", estados: ["approved"] },
  { clave: "revision", label: "En revisión", estados: ["received", "pending"] },
  { clave: "rechazadas", label: "Rechazadas", estados: ["rejected"] },
  { clave: "frenadas", label: "Frenadas", estados: ["paused", "disabled"] },
  { clave: "borradores", label: "Borradores", estados: ["borrador", "unsubmitted"] },
];

/** Minúsculas y sin acentos: «extrañamos» encuentra «Te extranamos» y al revés. */
function normalizar(s: string): string {
  return (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

const ICONO_TIPO: Record<TipoPlantilla, JSX.Element> = {
  "twilio/text": <MessageSquare size={14} />,
  "twilio/media": <ImageIcon size={14} />,
  "twilio/quick-reply": <Reply size={14} />,
  "twilio/call-to-action": <ExternalLink size={14} />,
  "whatsapp/card": <LayoutPanelTop size={14} />,
};

export default function PlantillasListaPage() {
  const [params, setParams] = useSearchParams();
  const { modo } = useCuentaOficial();
  const location = useLocation();
  const navigate = useNavigate();
  const filtroInicial = FILTROS_ESTADO.some((f) => f.clave === params.get("estado")) ? params.get("estado")! : "todas";

  const [lista, setLista] = useState<ListaPlantillas | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [incluirArchivadas, setIncluirArchivadas] = useState(false);
  const [filtro, setFiltro] = useState(filtroInicial);
  const [categoria, setCategoria] = useState<"" | "MARKETING" | "UTILITY">("");
  const [texto, setTexto] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [aviso, setAviso] = useState<string>(() => (location.state as { aviso?: string } | null)?.aviso || "");

  // El aviso (p. ej. «Plantilla borrada») se muestra una vez: se saca del historial para que un F5 no lo repita.
  useEffect(() => {
    if ((location.state as { aviso?: string } | null)?.aviso) navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Búsqueda con 250 ms de respiro: filtrar en cada tecla con 250 filas se nota en un celular viejo.
  useEffect(() => {
    const t = setTimeout(() => setBusqueda(texto), 250);
    return () => clearTimeout(t);
  }, [texto]);

  /** Pide la lista. Devuelve el error (texto) o "" si salió bien. */
  const pedir = useCallback(async (): Promise<string> => {
    try {
      const l = leerRespuesta<ListaPlantillas>(await oficialApi.plantillas({ incluirArchivadas }));
      if (!l.ok) return textoError(l);
      if (!l.data || !Array.isArray(l.data.items)) return "El servidor no devolvió la lista de plantillas.";
      setLista(l.data);
      return "";
    } catch (e) {
      return textoError(leerError(e));
    }
  }, [incluirArchivadas]);

  const cargar = useCallback(async () => {
    setCargando(true);
    const e = await pedir();
    setError(e);
    setCargando(false);
  }, [pedir]);

  useEffect(() => { void cargar(); }, [cargar]);

  const estados = useMemo(() => (lista?.items || []).map((p) => p.estado), [lista]);
  const polling = usePolling(async () => {
    const e = await pedir();
    if (e) throw new Error(e);
  }, lista ? intervaloPlantillas(estados) : null, [incluirArchivadas]);

  const cambiarFiltro = (clave: string) => {
    setFiltro(clave);
    const p = new URLSearchParams(params);
    if (clave === "todas") p.delete("estado"); else p.set("estado", clave);
    setParams(p, { replace: true });
  };

  const items = useMemo(() => lista?.items || [], [lista]);
  const { filtradas, aprobadasOcultas } = useMemo(() => {
    const estadosFiltro = FILTROS_ESTADO.find((f) => f.clave === filtro)?.estados ?? null;
    const q = normalizar(busqueda);
    let ocultas = 0;
    const visibles = items.filter((p) => {
      const pasa = (!estadosFiltro || estadosFiltro.includes(p.estado))
        && (!categoria || p.categoriaSolicitada === categoria || p.categoria === categoria)
        && (!q || normalizar(p.nombreVisible).includes(q) || normalizar(p.nombreTecnico).includes(q));
      // «Aprobadas» = las que sirven con la cuenta conectada (la misma regla que el asistente y el backend).
      if (pasa && filtro === "aprobadas" && motivoPlantillaInutilizable(p, modo)) { ocultas++; return false; }
      return pasa;
    });
    return { filtradas: primeroEnRevision(visibles), aprobadasOcultas: ocultas };
  }, [items, filtro, categoria, busqueda, modo]);

  const hayFiltros = filtro !== "todas" || !!categoria || !!busqueda.trim();
  // Sin ninguna plantilla no hay nada que filtrar: las pestañas, la búsqueda y la categoría no se muestran (auditoría de
  // diseño del 18/10/2026). «Mostrar archivadas» queda: puede haber archivadas, que no vienen por defecto.
  const sinNada = !!lista && items.length === 0 && !hayFiltros;
  const limpiar = () => { cambiarFiltro("todas"); setCategoria(""); setTexto(""); setBusqueda(""); };

  const cupo = lista?.cupo;
  const cupoPct = cupo && cupo.limite ? pct(cupo.usadas, cupo.limite) : 0;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 tracking-tight">Plantillas</h2>
          {cupo && (
            <p className={`text-xs mt-0.5 ${cupoPct >= 80 ? "text-amber-700 font-semibold" : "text-slate-500"}`}>
              {num(cupo.usadas)} de {num(cupo.limite)} {plural(cupo.limite, "plantilla", "plantillas")} que permite Meta
            </p>
          )}
        </div>
        {/* Crear plantillas (y mandarlas a Meta) es del equipo de Deenex: un cliente las ve. */}
        <SoloEquipo>
          <div className="flex gap-2">
            <Link to={`${R}/plantillas/modelos`}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <Sparkles size={15} /> Modelos Deenex
            </Link>
            <Link to={`${R}/plantillas/nueva`}
              className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700">
              <Plus size={15} /> Nueva plantilla
            </Link>
          </div>
        </SoloEquipo>
      </div>

      {aviso && (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1">{aviso}</span>
          <button type="button" onClick={() => setAviso("")} aria-label="Cerrar el aviso"><X size={14} /></button>
        </div>
      )}

      {/* Filtros */}
      <div className="space-y-3">
        {!sinNada && (
        <div role="tablist" aria-label="Estado" className="flex flex-wrap gap-1">
          {FILTROS_ESTADO.map((f) => (
            <button key={f.clave} type="button" role="tab" aria-selected={filtro === f.clave} onClick={() => cambiarFiltro(f.clave)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${filtro === f.clave ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {f.label}
            </button>
          ))}
        </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          {!sinNada && (<>
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar por nombre" aria-label="Buscar plantillas por nombre"
              className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
          <select value={categoria} onChange={(e) => setCategoria(e.target.value as "" | "MARKETING" | "UTILITY")} aria-label="Categoría"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
            <option value="">Todas las categorías</option>
            <option value="MARKETING">Marketing</option>
            <option value="UTILITY">Utilidad</option>
          </select>
          </>)}
          <label className="inline-flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={incluirArchivadas} onChange={(e) => setIncluirArchivadas(e.target.checked)}
              className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
            Mostrar archivadas
          </label>
        </div>
      </div>

      {polling.detenido && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle size={15} /> Dejamos de actualizar porque el servidor no responde.
          <button type="button" onClick={polling.reintentar} className="ml-auto font-semibold hover:underline">Reintentar</button>
        </div>
      )}

      {cargando && !lista ? (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 rounded-2xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : error && !lista ? (
        <Tarjeta>
          <p className="text-sm font-semibold text-red-700">No pudimos traer las plantillas.</p>
          <p className="text-sm text-slate-600 mt-1">{error}</p>
          <button type="button" onClick={() => void cargar()} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
            <RefreshCw size={14} /> Reintentar
          </button>
        </Tarjeta>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
          <FileText size={28} className="mx-auto text-slate-300" />
          <SoloEquipo siNo={<p className="text-sm text-slate-600 mt-3">Todavía no hay plantillas. Las arma el equipo de Deenex.</p>}>
            <p className="text-sm text-slate-600 mt-3">Todavía no hay plantillas. Empezá por un modelo de Deenex: ya pasa todas las reglas de Meta.</p>
            <div className="mt-4 flex justify-center gap-2">
              <Link to={`${R}/plantillas/modelos`}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <Sparkles size={15} /> Modelos Deenex
              </Link>
              <Link to={`${R}/plantillas/nueva`} className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700">
                <Plus size={15} /> Nueva plantilla
              </Link>
            </div>
          </SoloEquipo>
        </div>
      ) : filtradas.length === 0 && !aprobadasOcultas ? (
        <Tarjeta>
          <p className="text-sm text-slate-600">
            Ninguna plantilla coincide con el filtro.{" "}
            {hayFiltros && <button type="button" onClick={limpiar} className="font-semibold text-violet-700 hover:underline">Limpiar filtros</button>}
          </p>
        </Tarjeta>
      ) : (
        <>
          {/* Si el refresco falla pero hay una lista, se muestra la lista y el error arriba: nunca vacío. */}
          {error && <p className="text-xs text-red-600">No pudimos actualizar la lista: {error}</p>}
          {aprobadasOcultas > 0 && (
            <p className="text-xs text-amber-800">
              {aprobadasOcultas === 1 ? "1 aprobada no aparece: no sirve con esta cuenta" : `${num(aprobadasOcultas)} aprobadas no aparecen: no sirven con esta cuenta`}{" "}
              (simulada o de otra cuenta). Está en «Todas».
            </p>
          )}
          <ul className="space-y-2">
            {filtradas.map((p) => <FilaPlantilla key={p.id} p={p} modo={modo} />)}
          </ul>
        </>
      )}
    </div>
  );
}

function FilaPlantilla({ p, modo }: { p: PlantillaResumen; modo: string | null }) {
  // Con la cuenta real, una plantilla simulada o de otra cuenta figura «aprobada» pero no sirve
  // para una campaña (A1-4, A1-6): el chip lo dice en la lista, antes de que falle al confirmar.
  const noUsable = motivoPlantillaNoUsable(p, modo);
  // El chip de estado ya dice «Aprobada en simulado»: el de «Simulada» sobraría.
  const simuladaEnReal = p.estado === "approved" && motivoPlantillaInutilizable(p, modo) === "PLANTILLA_SIMULADA";
  const enRevision = enRevisionDeMeta(p.estado);
  const demorada = enRevision && typeof p.horasEnRevision === "number" && p.horasEnRevision > 48;
  // El ticket lo abre el equipo con el proveedor: al equipo, «avisale al equipo de Deenex» le hablaba de sí mismo.
  const equipo = useEsEquipoDeenex();
  const cat = p.categoria;
  return (
    <li className={p.archivada ? "opacity-60" : ""}>
      <Link to={`${R}/plantillas/${encodeURIComponent(p.id)}`}
        className="block rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm hover:border-slate-200 transition-colors">
        <div className="flex items-start gap-3 flex-wrap">
          <span className="w-8 h-8 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center flex-shrink-0" title={TIPO_PLANTILLA[p.tipo]?.titulo}>
            {ICONO_TIPO[p.tipo] ?? <MessageSquare size={14} />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 truncate">{p.nombreVisible}</span>
              <ChipEstadoPlantilla estado={p.estado} simulado={p.simulado} modo={modo} />
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${cat === "MARKETING" ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-600"}`}>
                {CATEGORIA[cat] ?? cat}
              </span>
              {p.recategorizada && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                  Meta la pasó a {CATEGORIA[p.recategorizada.a as keyof typeof CATEGORIA] ?? p.recategorizada.a}
                </span>
              )}
              {p.archivada && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Archivada</span>}
              {p.simulado === true && !simuladaEnReal && (
                <span title={CHIP_PLANTILLA.simulada.ayuda} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CHIP_PLANTILLA.simulada.cls}`}>
                  {CHIP_PLANTILLA.simulada.label}
                </span>
              )}
              {p.deOtraCuenta === true && (
                <span title={CHIP_PLANTILLA.deOtraCuenta.ayuda} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CHIP_PLANTILLA.deOtraCuenta.cls}`}>
                  {CHIP_PLANTILLA.deOtraCuenta.label}
                </span>
              )}
            </div>
            <p className="text-xs font-mono text-slate-400 mt-0.5 truncate">{p.nombreTecnico}</p>
            {noUsable && <p className="text-xs text-amber-800 mt-1">{noUsable}</p>}
          </div>
          <div className="text-right text-xs text-slate-500 space-y-0.5">
            {/* El costo real es lo que Deenex le paga a Meta y a Twilio; a la marca se le cobra con el recargo.
                A una persona de un cliente no le llega el costo real (respuesta-comercio.ts): ve sólo su precio. */}
            {p.costoPorMensajeUSD === undefined ? (
              <p><span className="font-semibold text-slate-700">{usdPorMensaje(p.costoPorMensajeClienteUSD)}</span> por mensaje</p>
            ) : (
              <>
                <p><span className="font-semibold text-slate-700">{usdPorMensaje(p.costoPorMensajeUSD)}</span> costo real por mensaje</p>
                {usdPorMensaje(p.costoPorMensajeClienteUSD) !== usdPorMensaje(p.costoPorMensajeUSD) && (
                  <p className="text-slate-400">A la marca: {usdPorMensaje(p.costoPorMensajeClienteUSD)}</p>
                )}
              </>
            )}
            <p>Usada en {num(p.usadaEnCampanas)} {plural(p.usadaEnCampanas, "campaña", "campañas")}</p>
            {enRevision && (
              <p className={demorada ? "text-amber-700 font-semibold" : ""}>
                {/* el ticket lo abre el equipo de Deenex con el proveedor: el comercio no sabe qué es Twilio */}
                En revisión {horasTexto(p.horasEnRevision)}{demorada ? (equipo ? " · abrí un ticket con el proveedor" : " · avisale a tu contacto de Deenex") : ""}
              </p>
            )}
          </div>
        </div>
      </Link>
    </li>
  );
}
